import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDateToDDMMYYYY } from "../pages/const";

const BORDER_COLOR = [140, 29, 26]; // #8c1d1a
const HEADER_BG = [240, 230, 210]; // #f0e6d2
const CREDIT_BG = [232, 245, 234]; // #e8f5ea
const DEBIT_BG = [252, 232, 234]; // #fce8ea
const BALANCE_BG = [232, 244, 247]; // #e8f4f7
const CONVERTS_BG = [227, 242, 253]; // #e3f2fd

const COLS = [
  { key: "date", label: "Date", w: 8, align: "center" },
  { key: "type", label: "Tag No", w: 10, align: "center" },
  { key: "credit", label: "Gold", w: 9, align: "right", kind: "weight" },
  { key: "silver_credit", label: "Silver", w: 9, align: "right", kind: "weight" },
  { key: "amount_credit", label: "Amount", w: 9, align: "right", kind: "money" },
  { key: "debit", label: "Gold", w: 9, align: "right", kind: "weight" },
  { key: "silver_debit", label: "Silver", w: 9, align: "right", kind: "weight" },
  { key: "amount_debit", label: "Amount", w: 9, align: "right", kind: "money" },
  { key: "balance", label: "Gold", w: 9, align: "right", kind: "balanceWeight" },
  { key: "silver_balance", label: "Silver", w: 9, align: "right", kind: "balanceWeight" },
  { key: "amount_balance", label: "Amount", w: 10, align: "right", kind: "balanceMoney" },
];

function formatDisplayDate(dateString) {
  if (!dateString || dateString === "-") return "-";
  const part = String(dateString).split(" ")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(part)) {
    const [y, m, d] = part.split("-");
    return `${d}-${m}-${y}`;
  }
  return formatDateToDDMMYYYY(part) || part;
}

function formatWeight(value) {
  if (value === "" || value == null || value === "-") return "-";
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "-";
  return Math.abs(n).toLocaleString("en-IN", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

function formatMoney(value) {
  if (value === "" || value == null || value === "-") return "-";
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "-";
  return Math.abs(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatBalanceWeight(value) {
  if (value === "" || value == null || value === "-") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  if (n === 0) return "0.000";
  const formatted = Math.abs(n).toLocaleString("en-IN", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
  return n < 0 ? `-${formatted}` : formatted;
}

function formatBalanceMoney(value) {
  if (value === "" || value == null || value === "-") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  if (n === 0) return "0.00";
  const formatted = Math.abs(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `-${formatted}` : formatted;
}

function formatColValue(col, row) {
  if (!row) return "-";
  if (col.key === "date") {
    if (row.isOpening) return "-";
    return formatDisplayDate(row.date);
  }
  if (col.key === "type") {
    return row.type || "-";
  }
  const raw = row[col.key];
  if (col.kind === "weight") return formatWeight(raw);
  if (col.kind === "money") return formatMoney(raw);
  if (col.kind === "balanceWeight") return formatBalanceWeight(raw);
  if (col.kind === "balanceMoney") return formatBalanceMoney(raw);
  if (raw == null || raw === "") return "-";
  return String(raw);
}

export function getFormattedGeneratedOn() {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = now.getFullYear();
  let hours = now.getHours();
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const formattedHours = String(hours).padStart(2, "0");
  return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
}

/**
 * Generates Karigar Ledger PDF Blob using high-performance jsPDF + autotable.
 * Supports exact 2-tier headers, Credited/Debited/Balance backgrounds,
 * balance coloring, converts row highlight, Total summary row (all black text), and Indian comma formatting.
 */
export async function generateKarigarLedgerPdfBlob({
  ledgerData = [],
  karigarInfo = {},
  fromDate = "",
  toDate = "",
  entireReport = false,
}) {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth(); // 841.89 pt
  const pageHeight = doc.internal.pageSize.getHeight(); // 595.28 pt
  const marginX = 14;
  const availableTableWidth = pageWidth - marginX * 2; // 813.89 pt

  // 2-tier header structure
  const head = [
    [
      { content: "Date", rowSpan: 2, styles: { halign: "center", valign: "middle", fillColor: HEADER_BG } },
      { content: "Tag No", rowSpan: 2, styles: { halign: "center", valign: "middle", fillColor: HEADER_BG } },
      { content: "Credited", colSpan: 3, styles: { halign: "center", valign: "middle", fillColor: CREDIT_BG, textColor: [0, 0, 0] } },
      { content: "Debited", colSpan: 3, styles: { halign: "center", valign: "middle", fillColor: DEBIT_BG, textColor: [0, 0, 0] } },
      { content: "Balance", colSpan: 3, styles: { halign: "center", valign: "middle", fillColor: BALANCE_BG, textColor: [0, 0, 0] } },
    ],
    [
      { content: "Gold", styles: { halign: "center", fillColor: CREDIT_BG } },
      { content: "Silver", styles: { halign: "center", fillColor: CREDIT_BG } },
      { content: "Amount", styles: { halign: "center", fillColor: CREDIT_BG } },
      { content: "Gold", styles: { halign: "center", fillColor: DEBIT_BG } },
      { content: "Silver", styles: { halign: "center", fillColor: DEBIT_BG } },
      { content: "Amount", styles: { halign: "center", fillColor: DEBIT_BG } },
      { content: "Gold", styles: { halign: "center", fillColor: BALANCE_BG } },
      { content: "Silver", styles: { halign: "center", fillColor: BALANCE_BG } },
      { content: "Amount", styles: { halign: "center", fillColor: BALANCE_BG } },
    ],
  ];

  // Map rows
  const body = ledgerData.map((row) =>
    COLS.map((col) => formatColValue(col, row))
  );

  // Column styles
  const columnStyles = {};
  COLS.forEach((col, idx) => {
    columnStyles[idx] = {
      cellWidth: (availableTableWidth * col.w) / 100,
      halign: col.align || "left",
    };
  });

  const partyName =
    karigarInfo?.companyName ||
    karigarInfo?.name ||
    karigarInfo?.label ||
    "-";
  const contactOrName = karigarInfo?.name || karigarInfo?.companyName || "-";
  const cityName = karigarInfo?.city || "-";

  let dateFilterText = "";
  if (entireReport) {
    dateFilterText = "Entire Report";
  } else if (fromDate || toDate) {
    const formattedFrom = fromDate ? formatDisplayDate(fromDate) : "-";
    const formattedTo = toDate ? formatDisplayDate(toDate) : "-";
    dateFilterText = `From: ${formattedFrom} To: ${formattedTo}`;
  }

  // Calculate totals for footer row
  let totCreditGold = 0;
  let totCreditSilver = 0;
  let totCreditAmount = 0;
  let totDebitGold = 0;
  let totDebitSilver = 0;
  let totDebitAmount = 0;

  ledgerData.forEach((row) => {
    if (!row.isOpening) {
      totCreditGold += parseFloat(row.credit || 0) || 0;
      totCreditSilver += parseFloat(row.silver_credit || 0) || 0;
      totCreditAmount += parseFloat(row.amount_credit || 0) || 0;
      totDebitGold += parseFloat(row.debit || 0) || 0;
      totDebitSilver += parseFloat(row.silver_debit || 0) || 0;
      totDebitAmount += parseFloat(row.amount_debit || 0) || 0;
    }
  });

  const lastRow = ledgerData.length > 0 ? ledgerData[ledgerData.length - 1] : null;
  const closingGoldBalance = lastRow ? parseFloat(lastRow.balance || 0) || 0 : 0;
  const closingSilverBalance = lastRow ? parseFloat(lastRow.silver_balance || 0) || 0 : 0;
  const closingAmountBalance = lastRow ? parseFloat(lastRow.amount_balance || 0) || 0 : 0;

  const foot = [
    [
      "",
      "TOTAL",
      formatWeight(totCreditGold),
      formatWeight(totCreditSilver),
      formatMoney(totCreditAmount),
      formatWeight(totDebitGold),
      formatWeight(totDebitSilver),
      formatMoney(totDebitAmount),
      formatBalanceWeight(closingGoldBalance),
      formatBalanceWeight(closingSilverBalance),
      formatBalanceMoney(closingAmountBalance),
    ],
  ];

  autoTable(doc, {
    startY: 56,
    head,
    body,
    foot,
    showFoot: "lastPage",
    theme: "grid",
    tableWidth: availableTableWidth,
    margin: { top: 56, left: marginX, right: marginX, bottom: 22 },
    styles: {
      font: "helvetica",
      fontSize: 7.5,
      cellPadding: { top: 3.5, bottom: 3.5, left: 3, right: 3 },
      lineColor: BORDER_COLOR,
      lineWidth: 0.5,
      textColor: [0, 0, 0],
      valign: "middle",
      overflow: "linebreak",
    },
    headStyles: {
      font: "helvetica",
      fontSize: 8,
      fillColor: HEADER_BG,
      textColor: [0, 0, 0],
      fontStyle: "bold",
      lineColor: BORDER_COLOR,
      lineWidth: 0.5,
      halign: "center",
      valign: "middle",
      cellPadding: { top: 3.5, bottom: 3.5, left: 2, right: 2 },
    },
    footStyles: {
      font: "helvetica",
      fontSize: 7.5,
      fillColor: HEADER_BG,
      textColor: [0, 0, 0],
      fontStyle: "bold",
      lineColor: BORDER_COLOR,
      lineWidth: 0.5,
      valign: "middle",
      cellPadding: { top: 3.5, bottom: 3.5, left: 2, right: 2 },
    },
    columnStyles,
    didParseCell: (data) => {
      if (data.section === "foot") {
        const colIdx = data.column.index;
        data.cell.styles.fillColor = HEADER_BG;
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.textColor = [0, 0, 0]; // Keep all total values (including last 3) black

        if (colIdx === 1) {
          data.cell.styles.halign = "center";
        }

        // Single-line auto-scale for numeric footer cells
        if (colIdx >= 2) {
          const rawText = Array.isArray(data.cell.text)
            ? data.cell.text.join(" ")
            : String(data.cell.text ?? "");

          const colDef = COLS[colIdx];
          const colWidth = (availableTableWidth * colDef.w) / 100;
          const maxTextWidth = colWidth - 6;

          doc.setFont("helvetica", "bold");
          let fontSize = 7.5;
          doc.setFontSize(fontSize);
          let measuredWidth = doc.getTextWidth(rawText);

          while (measuredWidth > maxTextWidth && fontSize > 4.5) {
            fontSize -= 0.3;
            doc.setFontSize(fontSize);
            measuredWidth = doc.getTextWidth(rawText);
          }

          data.cell.styles.fontSize = fontSize;
          data.cell.styles.overflow = "hidden";
          data.cell.text = [rawText];
        }
        return;
      }

      if (data.section === "body") {
        const row = ledgerData[data.row.index];
        const colIdx = data.column.index;
        if (!row) return;

        // Background styling
        if (row.pay_type === "CONVERTS") {
          data.cell.styles.fillColor = CONVERTS_BG;
        } else if (row.isOpening) {
          data.cell.styles.fillColor = CONVERTS_BG;
          data.cell.styles.fontStyle = "bold";
        } else {
          if (colIdx >= 2 && colIdx <= 4) {
            data.cell.styles.fillColor = CREDIT_BG;
          } else if (colIdx >= 5 && colIdx <= 7) {
            data.cell.styles.fillColor = DEBIT_BG;
          } else if (colIdx >= 8 && colIdx <= 10) {
            data.cell.styles.fillColor = BALANCE_BG;
          } else {
            data.cell.styles.fillColor = [255, 255, 255];
          }
        }

        // Text coloring for balance columns (8, 9, 10)
        if (!row.isOpening) {
          if (colIdx === 8) {
            const val = parseFloat(row.balance || 0);
            if (val < 0) data.cell.styles.textColor = [220, 53, 69]; // #dc3545
            else if (val > 0) data.cell.styles.textColor = [40, 167, 69]; // #28a745
          } else if (colIdx === 9) {
            const val = parseFloat(row.silver_balance || 0);
            if (val < 0) data.cell.styles.textColor = [220, 53, 69];
            else if (val > 0) data.cell.styles.textColor = [40, 167, 69];
          } else if (colIdx === 10) {
            const val = parseFloat(row.amount_balance || 0);
            if (val < 0) data.cell.styles.textColor = [220, 53, 69];
            else if (val > 0) data.cell.styles.textColor = [40, 167, 69];
          }
        }

        // Single-line auto-scale for numeric columns
        if (colIdx >= 2) {
          const rawText = Array.isArray(data.cell.text)
            ? data.cell.text.join(" ")
            : String(data.cell.text ?? "");

          const colDef = COLS[colIdx];
          const colWidth = (availableTableWidth * colDef.w) / 100;
          const maxTextWidth = colWidth - 6;

          doc.setFont("helvetica", data.cell.styles.fontStyle || "normal");
          let fontSize = data.cell.styles.fontSize || 7.5;
          doc.setFontSize(fontSize);
          let measuredWidth = doc.getTextWidth(rawText);

          while (measuredWidth > maxTextWidth && fontSize > 4.5) {
            fontSize -= 0.3;
            doc.setFontSize(fontSize);
            measuredWidth = doc.getTextWidth(rawText);
          }

          data.cell.styles.fontSize = fontSize;
          data.cell.styles.overflow = "hidden";
          data.cell.text = [rawText];
        }
      }
    },
    didDrawPage: (data) => {
      // Header for Page 1
      if (data.pageNumber === 1) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.setTextColor(BORDER_COLOR[0], BORDER_COLOR[1], BORDER_COLOR[2]);
        doc.text("Karigar Ledger Report", pageWidth / 2, 22, { align: "center" });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(30, 30, 30);
        const infoLine = `Karigar: ${partyName} (${contactOrName}) | City: ${cityName}`;
        doc.text(infoLine, pageWidth / 2, 36, { align: "center" });

        if (dateFilterText) {
          doc.setFontSize(8.5);
          doc.setTextColor(70, 70, 70);
          doc.text(dateFilterText, pageWidth / 2, 48, { align: "center" });
        }
      } else {
        // Concise title on subsequent pages
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(BORDER_COLOR[0], BORDER_COLOR[1], BORDER_COLOR[2]);
        doc.text(`Karigar Ledger Report - ${partyName}`, marginX, 20);
      }
    },
  });

  // Page numbering on all pages
  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Page ${page} of ${totalPages} (${ledgerData.length} records)`,
      pageWidth - marginX,
      20,
      { align: "right" }
    );
  }

  // On the last page, render "Generated on: DD-MM-YYYY hh:mm A" centered with a 14pt gap
  doc.setPage(totalPages);
  const finalY = doc.lastAutoTable?.finalY || 180;
  const footerY = Math.min(finalY + 14, pageHeight - 8);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(90, 90, 90);
  doc.text(
    `Generated on: ${getFormattedGeneratedOn()}`,
    pageWidth / 2,
    footerY,
    { align: "center" }
  );

  return doc.output("blob");
}
